import {
  Box,
  CardContent,
  Grid,
  Skeleton,
  Stack,
} from '@mui/material';

import CommonDashboardCard from '../commonDashboardCard';

function MetricCardSkeleton() {
  return (
    <CommonDashboardCard sx={{ minHeight: 160 }}>
      <CardContent sx={{ width: '100%', boxSizing: 'border-box' }}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Skeleton variant="circular" width={46} height={46} />

          <Box sx={{ flex: 1 }}>
            <Skeleton width="75%" height={22} />
            <Skeleton width="45%" height={38} />
            <Skeleton width="85%" height={18} />
          </Box>
        </Stack>

        <Skeleton variant="rounded" height={42} sx={{ mt: 1.5 }} />
      </CardContent>
    </CommonDashboardCard>
  );
}

function DashboardCardSkeleton({ height = 360, chart = false }) {
  return (
    <CommonDashboardCard sx={{ minHeight: height }}>
      <CardContent
        sx={{
          width: '100%',
          height: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Skeleton width="42%" height={28} />
        <Skeleton variant="rounded" height={1} sx={{ mb: 3 }} />

        {chart ? (
          <Skeleton
            variant="rounded"
            width="100%"
            height={height - 105}
          />
        ) : (
          <Stack direction="row" alignItems="center" spacing={3}>
            <Skeleton variant="circular" width={190} height={190} />

            <Stack spacing={1.5} sx={{ flex: 1 }}>
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton
                  key={index}
                  width={`${95 - index * 7}%`}
                  height={20}
                />
              ))}
            </Stack>
          </Stack>
        )}
      </CardContent>
    </CommonDashboardCard>
  );
}

function CardHeaderSkeleton({ titleWidth = '35%' }) {
  return (
    <Box sx={{ width: '100%', px: 2, pt: 1.5, boxSizing: 'border-box' }}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Skeleton variant="rounded" width={24} height={24} />
        <Skeleton width={titleWidth} height={28} />
      </Stack>

      <Skeleton variant="rounded" height={1} sx={{ mt: 1.25 }} />
    </Box>
  );
}

function PerformanceCardSkeleton() {
  return (
    <CommonDashboardCard
      sx={{
        minHeight: 360,
        alignItems: 'stretch',
        justifyContent: 'flex-start',
      }}
    >
      <CardHeaderSkeleton titleWidth="32%" />

      <CardContent sx={{ py: 0, width: '100%', boxSizing: 'border-box' }}>
        {Array.from({ length: 3 }).map((_, index) => (
          <Box key={index}>
            <Box sx={{ py: 1.5 }}>
              <Skeleton width="48%" height={20} />

              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box sx={{ flex: '1.55 1 0', minWidth: 0 }}>
                  <Skeleton width="38%" height={35} />
                  <Skeleton width="72%" height={16} />
                </Box>

                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{ flex: '1.9 1 0', minWidth: 0 }}
                >
                  <Skeleton
                    variant="rounded"
                    height={44}
                    sx={{ flex: 1, minWidth: 100 }}
                  />

                  <Box sx={{ width: 95, flexShrink: 0 }}>
                    <Skeleton width="35%" height={18} />
                    <Skeleton width="100%" height={15} />
                  </Box>
                </Stack>
              </Stack>
            </Box>

            {index < 2 && <Skeleton variant="rounded" height={1} />}
          </Box>
        ))}
      </CardContent>
    </CommonDashboardCard>
  );
}

function SlaCardSkeleton() {
  return (
    <CommonDashboardCard
      sx={{
        minHeight: 440,
        alignItems: 'stretch',
        justifyContent: 'flex-start',
      }}
    >
      <CardHeaderSkeleton titleWidth="12%" />

      <CardContent
        sx={{
          display: 'flex',
          alignItems: 'center',
          flex: 1,
          width: '100%',
          px: 4,
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 4,
            width: '100%',
          }}
        >
          {Array.from({ length: 3 }).map((_, index) => (
            <Stack key={index} direction="row" spacing={1.5} alignItems="flex-start">
              <Skeleton variant="rounded" width={24} height={28} />

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Skeleton width="88%" height={20} />
                <Skeleton width="34%" height={36} sx={{ mt: 0.5 }} />
                <Skeleton width="96%" height={16} />
              </Box>
            </Stack>
          ))}
        </Box>
      </CardContent>

      <Skeleton width={220} height={16} sx={{ ml: 2, mb: 1 }} />
    </CommonDashboardCard>
  );
}

export default function DashboardCardsSkeleton({
  loading = false,
  children,
}) {
  if (!loading) {
    return <>{children}</>;
  }

  return (
    <>
      <Grid container item xs={12} spacing={2}>
        {Array.from({ length: 6 }).map((_, index) => (
          <Grid key={index} item xs={2}>
            <MetricCardSkeleton />
          </Grid>
        ))}
      </Grid>

      <Grid container item xs={12} spacing={2}>
        <Grid item xs={4}>
          <DashboardCardSkeleton />
        </Grid>

        <Grid item xs={4}>
          <PerformanceCardSkeleton />
        </Grid>

        <Grid item xs={4}>
          <DashboardCardSkeleton />
        </Grid>
      </Grid>

      <Grid container item xs={12} spacing={2}>
        <Grid item xs={6}>
          <SlaCardSkeleton />
        </Grid>

        <Grid item xs={6}>
          <DashboardCardSkeleton height={440} chart />
        </Grid>
      </Grid>
    </>
  );
}
